import { useState } from 'react';
import { userApi, type User } from '@src/api/client';

type Props = {
  user: User;
  onUpdate: () => void;
};

const TIER_LIMITS: Record<string, { apps: number; tailors: number }> = {
  free: { apps: 5, tailors: 3 },
  pro: { apps: 50, tailors: 25 },
  premium: { apps: 200, tailors: 100 },
};

const SettingsPage = ({ user, onUpdate }: Props) => {
  const [displayName, setDisplayName] = useState(user.displayName || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await userApi.update({ displayName });
      setMessage('Settings saved');
      onUpdate();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const limits = TIER_LIMITS[user.subscriptionTier] || TIER_LIMITS.free;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-gray-500">Manage your account preferences</p>
      </div>

      <div className="max-w-2xl space-y-6">
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">Profile</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <p className="mt-1 text-sm text-gray-500">{user.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Display Name</label>
              <input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
              />
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-brand-500 hover:bg-brand-600 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            {message && (
              <p className={`text-sm ${message === 'Settings saved' ? 'text-green-600' : 'text-red-600'}`}>{message}</p>
            )}
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">Subscription</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Current Plan:</span>
              <span className="bg-brand-100 text-brand-700 rounded-full px-3 py-1 text-sm font-medium capitalize">
                {user.subscriptionTier}
              </span>
            </div>
            <div className="rounded-lg bg-gray-50 p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-gray-500">Daily Applications</p>
                  <p className="text-lg font-semibold">
                    {user.applicationsToday} / {limits.apps}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Daily Tailors</p>
                  <p className="text-lg font-semibold">
                    {user.tailorsToday} / {limits.tailors}
                  </p>
                </div>
              </div>
            </div>
            {user.subscriptionTier === 'free' && (
              <a
                href="https://resumeapplier.com/pricing/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-brand-500 hover:bg-brand-600 inline-block rounded-lg px-4 py-2 text-sm font-medium text-white">
                Upgrade Plan
              </a>
            )}
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">Chrome Extension</h2>
          <p className="text-sm text-gray-600">
            Install the Chrome extension to auto-apply to jobs, auto-fill application forms, and tailor resumes directly
            from job sites.
          </p>
          <a
            href="https://resumeapplier.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-block rounded-lg border px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Get the Extension
          </a>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
